import React from 'react';
import { useTranslation } from 'react-i18next';
import { Text, Spacing, Icon } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import usePurchase from 'hooks/store/usePurchase';
import PointsSlider from 'ui/molecules/PointsSlider';
import useWhitelabelEnvs from 'hooks/whitelabel/useWhitelabelEnvs';
import useWhitelabelFeatures from 'hooks/whitelabel/useWhitelabelFeatures';
import { openTpFundWidget } from '../../TpFundWidget';

import useLoyaltyPrograms from '../../../loyalty/context/useLoyaltyPrograms';
import useCostaPassSlider from './useCostaPassSlider';

/**
 * CostaPassSlider component.
 * Render a slider to manage CostaPass/TravelPass wallet balance for purchases.
 * @returns {JSX.Element|null} The rendered component or null if conditions are not met.
 */
const CostaPassSlider = () => {
  const purchase = usePurchase();
  const features = useWhitelabelFeatures();
  const {
    selectedLoyaltyProgram,
    costaPass: {
      profile,
      wallet,
      onApplyPointsDiscount,
      onRemovePointsDiscount,
      onRefreshWalletBalance,
      config: { brands, rechargeURL: defaultRechargeURL },
    },
  } = useLoyaltyPrograms();
  const url = brands?.[profile.brand]?.urls?.recharge || defaultRechargeURL;
  const rechargeURL = `${url}?token=${profile.token}`;

  const { travelpassLoyaltyConfig, tripPriceDecimals } = useWhitelabelEnvs();
  const decimals =
    Number.isInteger(tripPriceDecimals) && tripPriceDecimals >= 0 ? tripPriceDecimals : 0;

  const {
    sliderPoints,
    canApplyPoints,
    error,
    disabled,
    handleOnChange,
    handleOnEdit,
    handleOnCancel,
    handleOnDelete,
    handleOnSubmitPoints,
    replacesRegexComas,
    formattedMinUsableBalance,
    formattedUsableBalance,
    formattedBalance,
    totalBalance,
    remainingBalance,
  } = useCostaPassSlider({
    wallet,
    onApplyPointsDiscount,
    onRemovePointsDiscount,
    purchaseTotal: purchase.total,
    purchaseTotalBeforeLock: purchase.totalBeforeLock,
    decimals,
  });

  const { t: tPayment } = useTranslation('payment');
  const prefix = travelpassLoyaltyConfig?.prefix || 'costapass';
  const enableFundWidget = features.TP_FUND_WIDGET_ENABLED;
  const showInput = features.WALLET_SLIDER_INPUT_ENABLED;

  const headerTitle = !wallet.balance
    ? tPayment('label.add_money_costapass_title', { context: prefix })
    : tPayment('label.select_costapass_money_to_use', { context: prefix });

  if (selectedLoyaltyProgram !== 'travelpass' && selectedLoyaltyProgram !== 'costapass')
    return null;

  const notPointsMessage = `label.add_money_costapass_instructions_${prefix}`;

  return (
    <PointsSlider
      enableFundWidget={enableFundWidget}
      handleOpenFundWidget={openTpFundWidget}
      inputValue={String(sliderPoints)}
      minPoints={wallet.minUsableBalance}
      maxPoints={wallet.maxUsableBalance}
      showInput={showInput}
      handleOnSubmitPoints={handleOnSubmitPoints}
      handleOnChange={handleOnChange}
      handleOnDelete={handleOnDelete}
      handleOnEdit={handleOnEdit}
      handleOnCancel={handleOnCancel}
      sliderPoints={Number(sliderPoints).toFixed(2).toString()}
      inputPointsValue={sliderPoints}
      balance={totalBalance}
      remainingBalance={remainingBalance}
      showRemainingBalance
      disabled={disabled}
      pointsApplied={wallet.usedBalance}
      isLoading={wallet.isUpdating || purchase.isUpdating}
      headerTitle={headerTitle}
      notPoints={!wallet.balance}
      notPointsMessage={notPointsMessage}
      sliderHeader={
        <Spacing justifyContent="center" alignItems="center" vertical size="S">
          <p className="flex items-center gap-2 font-bold">
            <Text size="M" mobileSize="S" weight="regular" elementType="span">
              {tPayment('label.balance_intro')}
            </Text>
            <span className="bg-gray-200/50 rounded-2xl px-2 py-1 font-bold">
              ${formattedBalance}
            </span>{' '}
            <Text size="M" mobileSize="S" weight="regular" elementType="span">
              {tPayment('label.balance_outro')}
            </Text>
            <Icon type="emojiSparkles" size="S" />
          </p>
          {canApplyPoints ? (
            <LocalizedCurrency
              size="L"
              mobileSize="M"
              weight="semibold"
              color="primary"
              price={Number(sliderPoints) || 0}
              currencyStyle="currency"
              currencyLowercase
            />
          ) : (
            <Text color="grayMedium" size="M" mobileSize="S">
              {tPayment('label.you_can_exchange_costapass_money', {
                points: formattedUsableBalance,
              })}
            </Text>
          )}
        </Spacing>
      }
      inputLabelText={tPayment('label.costapass_to_use')}
      minPointsText={`${formattedMinUsableBalance} MXN`}
      maxPointsText={`${formattedUsableBalance} MXN`}
      pointsAppliedText={tPayment('label.points_applied_costapass', {
        pointsUsed: wallet.usedBalance.toFixed(2).toString().replace(replacesRegexComas, ','),
        context: prefix,
      })}
      errorMessage={error}
      editButtonText={tPayment('button.modify_costapass_money')}
      modifierClass={prefix}
      rechargeURL={rechargeURL}
      steps="0.01"
      showAddPointsButton
      canApplyPoints={canApplyPoints}
      loyalty={prefix}
      deleteButtonText={tPayment('button.delete_costapass_money')}
      handleUpdateWallet={onRefreshWalletBalance}
    />
  );
};

export default CostaPassSlider;
